// Contracts — javelin sessionState (the moderator boot + refetch read).
// Worker side: apps/api/src/javelin/setup.ts getSessionState(). Client
// side: apps/internal javelin island — boot (app/boot.ts) AND every
// refresh (features/session/lib.ts refreshSession). Snapped from the
// worker's response literal during block G; loose objects so unknown
// extra fields pass through. This read carries the island's standing
// invariants (an Open location is always visible on the dashboard, the
// location photo key is always current) — drift must surface instead
// of silently breaking those behaviours.
import { z } from 'zod';

export const JavSessionParticipantSchema = z.looseObject({
  participant_id: z.coerce.string(),
  name: z.string().default(''),
  hasIntake: z.boolean().default(false),
  hasAppearance: z.boolean().default(false),
  hasSession: z.boolean().default(false),
  hasPack: z.boolean().default(false),
  intake_at: z.string().default(''),
  skin_tone: z.union([z.string(), z.number()]).default(''),
  gender: z.union([z.literal('M'), z.literal('F'), z.literal('O'), z.literal('')]).catch(''),
  age: z.number().default(0),
});

export const JavSessionLocationSchema = z.looseObject({
  home_id: z.coerce.string(),
  status: z.string().catch(''),
  photo_r2_key: z.string().catch(''),
  locSetupDone: z.boolean().default(false),
  participants: z.array(JavSessionParticipantSchema).default([]),
  filledSlots: z.number().default(0),
  maxSlots: z.number(),
  room_type: z.string().optional(),
  lighting: z.string().optional(),
  chair_type: z.string().optional(),
  chair_arm: z.string().optional(),
  chair_seat_height: z.string().optional(),
  lux_per_camera_json: z.string().optional(),
  mirror_per_camera_json: z.string().optional(),
  clutter_per_camera_json: z.string().optional(),
  hp_per_camera_json: z.string().optional(),
  height_per_camera_json: z.string().optional(),
});

// live_participants is not emitted by the worker today (checked
// 2026-06-10) but the client keeps the read — pin the shape so a future
// emit lands validated.
export const JavLiveParticipantSchema = z.looseObject({
  participant_id: z.coerce.string(),
  name: z.string().default(''),
  intake_at: z.string().catch(''),
  skin_tone: z.coerce.string().catch(''),
  gender: z.union([z.literal('M'), z.literal('F'), z.literal('O'), z.literal('')]).catch(''),
  age: z.number().default(0),
});

export const JavSessionStateResSchema = z.looseObject({
  mod_id: z.coerce.string().default(''),
  mod_name: z.coerce.string().default(''),
  roles: z.array(z.string()).default([]),
  is_owner: z.boolean().default(false),
  locations: z.array(JavSessionLocationSchema).default([]),
  live_participants: z.array(JavLiveParticipantSchema).optional(),
});

export type JavSessionStateRes = z.infer<typeof JavSessionStateResSchema>;
